import React, { useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, Paper, Radio,
  RadioGroup, TextField, Typography,
} from '@mui/material';
import { settleVideoCase } from '../api';
import { useEnv } from '../contexts/EnvContext';
import { ENVIRONMENT_NAMES } from './StuckClocks';

const NOTE_MAX = 1000;

function storedEnv() {
  try {
    return localStorage.getItem('bidsy-env') || 'production';
  } catch {
    return null;
  }
}

/**
 * Settling a case (admin panel plan RPT-18, RPT-19): the outcomes Bidsy
 * allows for this case, each in Bidsy's own words; a staff note shown to
 * nobody; a confirm that names the two people, the tokens, the outcome and
 * the environment. Bidsy's settleCase moves the money; the panel works out
 * none, and writes are allowed on production (rule 4).
 */
export default function SettleCase({
  kase, fan, creator, outcomes, settled = false, onSettled,
}) {
  const { env } = useEnv();
  const [choice, setChoice] = useState('');
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const chosen = outcomes.find((each) => each.outcome === choice && !each.blocked) || null;

  const settle = async () => {
    if (storedEnv() !== env) {
      setConfirming(false);
      setResult({ severity: 'error', text: 'The environment was switched in another tab. Reload this page, then settle the case.' });
      return;
    }
    setRunning(true);
    try {
      const answer = await settleVideoCase(kase.id, { outcome: choice, staff_note: note.trim() || null });
      setResult({ severity: 'success', text: 'Settled.', moved: answer?.data?.moved || [] });
    } catch (e) {
      const code = e?.response?.data?.code;
      setResult({
        // Already settled is an answer; busy means nothing moved and a retry may go; anything else is a fault.
        severity: { VIDEO_CASE_ALREADY_SETTLED: 'info', VIDEO_BOOKING_BUSY: 'warning' }[code] || 'error',
        text: e?.response?.data?.message || e?.message || 'The case could not be settled.',
      });
    } finally {
      setRunning(false);
      setConfirming(false);
      if (onSettled) onSettled();
    }
  };

  const answer = result ? (
    <Alert severity={result.severity} onClose={() => setResult(null)} sx={{ mb: 1 }}>
      {result.text}
      {result.moved?.length ? <Box component="ul" sx={{ m: 0, pl: 2 }}>{result.moved.map((line) => <li key={line}>{line}</li>)}</Box> : null}
    </Alert>
  ) : null;
  if (settled) return answer;
  if (!outcomes.length) return null;

  return (
    <Paper sx={{ p: 2, mb: 2, borderLeft: 4, borderColor: 'warning.main' }} data-testid="settle-case">
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Settle this case</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        The case holds {Number(kase.tokens_held || 0).toLocaleString()} tokens. There is no deadline.
      </Typography>
      {answer}
      <RadioGroup value={choice} onChange={(event) => setChoice(event.target.value)}>
        {outcomes.map((each) => (
          <FormControlLabel
            key={each.outcome}
            value={each.outcome}
            disabled={Boolean(each.blocked)}
            control={<Radio size="small" />}
            label={(
              <Box sx={{ py: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{each.outcome}{each.applies ? ` (${each.applies})` : ''}</Typography>
                <Typography variant="body2" color="text.secondary">{each.words}</Typography>
                {each.blocked ? <Typography variant="body2" color="error">{each.blocked}</Typography> : null}
              </Box>
            )}
          />
        ))}
      </RadioGroup>
      <TextField
        label="Staff note (for staff only; never sent to either person)"
        value={note}
        onChange={(event) => setNote(event.target.value.slice(0, NOTE_MAX))}
        helperText={`${note.length} / ${NOTE_MAX}`}
        fullWidth
        multiline
        minRows={2}
        size="small"
        sx={{ my: 1 }}
      />
      <Button variant="contained" color="warning" disabled={!chosen} onClick={() => setConfirming(true)}>Settle…</Button>

      <Dialog open={confirming} onClose={() => !running && setConfirming(false)}>
        <DialogTitle>Settle case #{kase.id} on {ENVIRONMENT_NAMES[env] || env}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {fan?.username} and {creator?.username} · {Number(kase.tokens_held || 0).toLocaleString()} tokens
          </DialogContentText>
          <DialogContentText sx={{ mt: 1, fontWeight: 600 }}>
            {chosen?.outcome}{chosen?.applies ? ` (${chosen.applies})` : ''}
          </DialogContentText>
          <DialogContentText>{chosen?.words}</DialogContentText>
          <DialogContentText sx={{ mt: 1 }}>Bidsy tells the people concerned the outcome. This cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)} disabled={running}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={settle} disabled={running}>
            Settle on {env === 'development' ? 'DEV' : 'PROD'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
